// The editors a Library row needs beyond a text box (Spencer's change log, 2026-10-07):
// a choice standard's choices as one table (each choice with its rule and the material it
// stands for in every role), what a material standard's picker offers, a standard's default
// picked the way its type calls for, and the keys a material is offered under. Each writes
// one column of one row.

import { useState } from 'react';
import { parse, type Library, type MaterialFilter } from '@zerogap/engine';
import { updateRow, type Row } from '../lib/api';
import { useLibrary } from '../lib/library';
import { Btn, Edit, Empty, Info, Loading } from '../ui/kit';
import { catalogFits, FilterEditor, filterWords, materialFits } from '../ui/materialfilter';
import { Grip, useDragOrder } from '../ui/move';
import { ListEdit } from '../ui/settingvalue';
import { CatalogPick, MaterialPick, RecipePick } from './job/ItemTab';

/** A formula is kept only if the engine can read it. */
const formula = (t: string) => {
  if (t.trim() === '') return '';
  try {
    parse(t);
    return t.trim();
  } catch {
    return undefined;
  }
};
const snake = (t: string) => (/^[a-z][a-z0-9_]*$/.test(t.trim()) ? t.trim() : undefined);

interface Choice {
  value: string;
  note?: string;
  materials?: Record<string, string>;
  unavailable_expr?: string | null;
}

/** The material roles the catalog reads through a job standard: on BOM lines and parts, less the roles that are material standards or material inputs of their own. */
function catalogRoles(lib: Library): string[] {
  const direct = new Set<string>();
  for (const d of lib.standards) if (d.data_type === 'material') direct.add(d.key);
  for (const c of Object.values(lib.catalog)) for (const i of c.inputs) if (i.data_type === 'material') direct.add(i.key);
  const roles = new Set<string>();
  for (const c of Object.values(lib.catalog)) {
    for (const r of c.recipes) {
      for (const b of r.bom) if (b.material_role && !direct.has(b.material_role)) roles.add(b.material_role);
      for (const p of r.parts) if (p.material_role && !direct.has(p.material_role)) roles.add(p.material_role);
    }
  }
  return [...roles].sort();
}

/**
 * A choice standard: the roles it fills (each with what its pickers offer), then one table with a row per
 * choice: the choice, the rule that makes it unavailable, a material for every role, drag to reorder, remove.
 * Spencer, 2026-10-07: a table that goes out to the right, never a stack.
 */
export function StandardChoices({ row, disabled }: { row: Row; disabled: boolean }) {
  const { lib } = useLibrary();
  const [adding, setAdding] = useState('');
  const [addingRole, setAddingRole] = useState('');
  const choices: Choice[] = Array.isArray(row.choices) ? (row.choices as Choice[]) : [];
  const saveChoices = (next: Choice[]) => updateRow('standard_def', row.id, { choices: next });
  const drag = useDragOrder((id, target) => {
    const from = choices.findIndex((c) => c.value === id);
    const to = choices.findIndex((c) => c.value === target);
    if (from < 0 || to < 0) return;
    const next = [...choices];
    const [c] = next.splice(from, 1);
    next.splice(to, 0, c!);
    return saveChoices(next);
  }, disabled);
  if (row.data_type !== 'choice') return <small className="muted">Choices belong to a choice standard. A material, catalog item or recipe standard picks one row from the library instead.</small>;
  if (!lib) return <Loading />;
  const roles: Record<string, MaterialFilter | null> = row.roles && typeof row.roles === 'object' ? (row.roles as Record<string, MaterialFilter | null>) : {};
  const roleNames = [...new Set([...Object.keys(roles), ...choices.flatMap((c) => Object.keys(c.materials ?? {}))])];
  const readRoles = catalogRoles(lib).filter((r) => !roleNames.includes(r));
  const saveRoles = (next: Record<string, MaterialFilter | null>) => updateRow('standard_def', row.id, { roles: Object.keys(next).length ? next : null });
  const has = (v: string, except?: string) => choices.some((c) => c.value !== except && c.value.toLowerCase() === v.toLowerCase());
  const blanks = choices.reduce((n, c) => n + roleNames.filter((r) => !c.materials?.[r]).length, 0);
  const setMaterial = (i: number, r: string, mid: string | null) =>
    saveChoices(
      choices.map((x, k) => {
        if (k !== i) return x;
        const m = { ...(x.materials ?? {}) };
        if (mid) m[r] = mid;
        else delete m[r];
        return { ...x, materials: Object.keys(m).length ? m : undefined };
      }),
    );

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div>
        <div className="lb">
          <span>
            Roles this standard fills ({roleNames.length})
            <Info>
              The material roles a choice stands for: the names the catalog's lines and parts read ("the job's carcass 3/4"). Each role says what its pickers offer: a key the materials carry, a category, or both. A role the catalog reads and this standard does not fill yet is offered under + Role.
            </Info>
          </span>
        </div>
        {roleNames.length === 0 && <small>No roles yet: this standard's choices are names only, and fill no material line.</small>}
        {roleNames.map((r) => (
          <div key={r} className="row mid" style={{ gap: 6, padding: '2px 0', flexWrap: 'wrap' }}>
            <code style={{ minWidth: 140 }}>{r}</code>
            <FilterEditor lib={lib} value={roles[r] ?? null} disabled={disabled} onChange={(f) => void saveRoles({ ...roles, [r]: f })} />
            {!disabled && (
              <Btn
                small
                title="Remove this role from the standard"
                onClick={() => {
                  if (!window.confirm(`Remove the role "${r}"? Every choice's material for it is removed too; lines that read it price at zero until a choice names one again.`)) return;
                  const nextRoles = { ...roles };
                  delete nextRoles[r];
                  void saveRoles(nextRoles);
                  void saveChoices(
                    choices.map((c) => {
                      if (!c.materials?.[r]) return c;
                      const m = { ...c.materials };
                      delete m[r];
                      return { ...c, materials: Object.keys(m).length ? m : undefined };
                    }),
                  );
                }}
              >
                ×
              </Btn>
            )}
          </div>
        ))}
        {!disabled && (
          <div className="row mid" style={{ gap: 6, marginTop: 4 }}>
            <input className="cell mono" list={`roles-${row.id}`} placeholder="Role name, as a line or part reads it (carcass_3_4)" value={addingRole} onChange={(e) => setAddingRole(e.target.value)} style={{ maxWidth: 360 }} />
            <datalist id={`roles-${row.id}`}>
              {readRoles.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
            <Btn
              small
              disabled={!snake(addingRole) || roleNames.includes(addingRole.trim())}
              onClick={() => {
                void saveRoles({ ...roles, [addingRole.trim()]: null });
                setAddingRole('');
              }}
            >
              + Role
            </Btn>
            {readRoles.length > 0 && <small>Read by the catalog and not filled here: {readRoles.join(', ')}.</small>}
          </div>
        )}
      </div>

      <div>
        <div className="lb">
          <span>
            Choices ({choices.length})
            {blanks > 0 && <small> · {blanks} blank cell{blanks === 1 ? '' : 's'}: a choice prices that role at zero until a material is picked</small>}
            <Info>
              One row per choice, in the order a job's picker lists them; drag the grip to reorder. <b>Unavailable when</b> is a formula over the job's standards; when true the choice is grayed in the picker with the rule beside it, and a job already holding it is warned (white melamine: box_build &lt;&gt; 'Cabinotch'). Each role column is the material the choice stands for in that role. Renaming a choice does not change a job that already holds the old name.
            </Info>
          </span>
        </div>
        <div className="tblwrap">
          <table className="fixed" style={{ minWidth: 420 + roleNames.length * 350 }}>
            <colgroup>
              <col style={{ width: 28 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 190 }} />
              {roleNames.map((r) => (
                <col key={r} style={{ width: 350 }} />
              ))}
              <col style={{ width: 36 }} />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Choice</th>
                <th>Unavailable when</th>
                {roleNames.map((r) => (
                  <th key={r}>
                    {r.replace(/_/g, ' ')}
                    {filterWords(roles[r]) && <small> · {filterWords(roles[r])}</small>}
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {choices.map((c, i) => (
                <tr key={c.value} {...drag.row(c.value)}>
                  <td>
                    <Grip {...drag.grip(c.value)} />
                  </td>
                  <td>
                    <Edit className="cell" disabled={disabled} value={c.value} parse={(t) => (t.trim() && !has(t.trim(), c.value) ? t.trim() : undefined)} onSave={(v) => saveChoices(choices.map((x, k) => (k === i ? { ...x, value: v } : x)))} />
                    {c.note && <small className="muted">{c.note}</small>}
                  </td>
                  <td>
                    <Edit className="cell mono" disabled={disabled} value={c.unavailable_expr ?? ''} parse={formula} onSave={(v) => saveChoices(choices.map((x, k) => (k === i ? { ...x, unavailable_expr: v || null } : x)))} />
                  </td>
                  {roleNames.map((r) => {
                    const id = c.materials?.[r] ?? '';
                    const m = id ? lib.materials[id] : undefined;
                    const fits = !m || materialFits(m, roles[r]);
                    return (
                      <td key={r}>
                        {disabled ? (
                          m?.description ?? <span className="muted">—</span>
                        ) : (
                          <span className="row mid" style={{ gap: 2, flexWrap: 'nowrap' }}>
                            <MaterialPick lib={lib} value={id} filter={roles[r]} onPick={(mid) => void setMaterial(i, r, mid)} />
                            {id && (
                              <Btn small title="Clear this cell" onClick={() => void setMaterial(i, r, null)}>
                                ×
                              </Btn>
                            )}
                          </span>
                        )}
                        {!fits && <div className="warn">Outside this role's filter.</div>}
                      </td>
                    );
                  })}
                  <td>
                    {!disabled && (
                      <Btn small title="Remove this choice" onClick={() => window.confirm(`Remove the choice "${c.value}"? A job already holding it keeps the name until it is changed.`) && void saveChoices(choices.filter((_, k) => k !== i))}>
                        ×
                      </Btn>
                    )}
                  </td>
                </tr>
              ))}
              {choices.length === 0 && (
                <tr>
                  <td colSpan={4 + roleNames.length}>
                    <small>No choices yet.</small>
                  </td>
                </tr>
              )}
              {!disabled && (
                <tr>
                  <td></td>
                  <td colSpan={3 + roleNames.length}>
                    <span className="row mid" style={{ gap: 6 }}>
                      <input
                        className="cell"
                        placeholder="New choice"
                        value={adding}
                        style={{ maxWidth: 260 }}
                        onChange={(e) => setAdding(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && adding.trim() && !has(adding.trim())) {
                            void saveChoices([...choices, { value: adding.trim() }]);
                            setAdding('');
                          }
                        }}
                      />
                      <Btn
                        small
                        disabled={!adding.trim() || has(adding.trim())}
                        onClick={() => {
                          void saveChoices([...choices, { value: adding.trim() }]);
                          setAdding('');
                        }}
                      >
                        + Add choice
                      </Btn>
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** What a material standard's picker offers; for a catalog-item standard, the family it keeps to. Nothing for the other types. */
export function StandardFilter({ row, disabled }: { row: Row; disabled: boolean }) {
  const { lib } = useLibrary();
  if (row.data_type === 'catalog_item') {
    if (!lib) return <Loading />;
    const filter = (row.material_filter ?? null) as MaterialFilter | null;
    const families = [...new Set(Object.values(lib.catalog).map((c) => c.family).filter((f): f is string => !!f))].sort((a, b) => a.localeCompare(b));
    const n = Object.values(lib.catalog).filter((c) => !c.retired && catalogFits(c, filter)).length;
    return (
      <span className="row mid" style={{ gap: 6, flexWrap: 'wrap' }}>
        <select disabled={disabled} value={filter?.family ?? ''} onChange={(e) => void updateRow('standard_def', row.id, { material_filter: e.target.value ? { family: e.target.value } : null })} title="Only catalog items of one family (Door type A: Doors/Panels)">
          <option value="">any family</option>
          {families.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
          {filter?.family && !families.includes(filter.family) && <option value={filter.family}>{filter.family} (no item in it yet)</option>}
        </select>
        <small>
          {n} item{n === 1 ? '' : 's'} offered
        </small>
      </span>
    );
  }
  if (row.data_type !== 'material') return <small className="muted">For a material or catalog-item standard: a choice standard's roles each carry their own, below.</small>;
  if (!lib) return <Loading />;
  const n = Object.values(lib.materials).filter((m) => materialFits(m, row.material_filter ?? null)).length;
  return (
    <span className="row mid" style={{ gap: 6, flexWrap: 'wrap' }}>
      <FilterEditor lib={lib} value={row.material_filter ?? null} disabled={disabled} onChange={(f) => void updateRow('standard_def', row.id, { material_filter: f })} />
      <small>
        {n} material{n === 1 ? '' : 's'} offered
      </small>
    </span>
  );
}

/** A standard's company default, picked the way its type calls for. */
export function StandardDefault({ row, disabled }: { row: Row; disabled: boolean }) {
  const { lib } = useLibrary();
  const save = (v: string | null) => updateRow('standard_def', row.id, { default_value: v || null });
  const value = (row.default_value as string | null) ?? '';
  if (row.data_type === 'choice') {
    const choices: Choice[] = Array.isArray(row.choices) ? (row.choices as Choice[]) : [];
    return (
      <select disabled={disabled} value={value} onChange={(e) => void save(e.target.value)}>
        <option value="">none</option>
        {choices.map((c) => (
          <option key={c.value} value={c.value}>
            {c.value}
          </option>
        ))}
        {value && !choices.some((c) => c.value === value) && <option value={value}>{value} (not one of the choices)</option>}
      </select>
    );
  }
  if (row.data_type === 'boolean') {
    return (
      <select disabled={disabled} value={value} onChange={(e) => void save(e.target.value)}>
        <option value="">none</option>
        <option value="true">yes</option>
        <option value="false">no</option>
      </select>
    );
  }
  if (!lib) return <Loading />;
  if (row.data_type === 'material') return <MaterialPick lib={lib} value={value} filter={row.material_filter ?? null} onPick={(id) => void save(id)} />;
  if (row.data_type === 'catalog_item') return <CatalogPick lib={lib} value={value} onPick={(id) => void save(id)} />;
  if (row.data_type === 'recipe') return <RecipePick lib={lib} value={value} onPick={(name) => void save(name)} />;
  return <Edit disabled={disabled} value={value} onSave={(v) => save(v)} />;
}

/** The keys a material is offered under: a list, added to and removed like a family. The keys in use elsewhere are offered as you type. */
export function OfferedAs({ row, disabled }: { row: Row; disabled: boolean }) {
  const { lib } = useLibrary();
  const value: string[] = Array.isArray(row.offered_as) ? (row.offered_as as string[]) : [];
  const others = lib ? [...new Set(Object.values(lib.materials).flatMap((m) => m.offered_as ?? []))].filter((k) => !value.includes(k)).sort() : [];
  return (
    <div>
      <ListEdit value={value} disabled={disabled} hooks={{ one: 'key' }} onSave={(v) => updateRow('material', row.id, { offered_as: v })} />
      {others.length > 0 && !disabled && (
        <small>
          Keys in use:{' '}
          {others.map((k) => (
            <button key={k} type="button" className="tag off" style={{ marginRight: 4, cursor: 'pointer' }} onClick={() => void updateRow('material', row.id, { offered_as: [...value, k] })}>
              + {k}
            </button>
          ))}
        </small>
      )}
      {value.length === 0 && others.length === 0 && <Empty>No key yet. A key names a set a picker offers: exterior sheet, edge banding.</Empty>}
    </div>
  );
}
